import React, { useEffect } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { useGroupQuery } from "generated/graphql";
import { Breadcrumbs, Button, Header, LoadingOverlay, PageContent, PageHeader, Spacer } from "components";
import { GroupInfoBlock } from "components/MyLivestock";
import { ELEMENTS_IDS } from "constants/ElementsIds";
import { GROUPS, GROUP_VIEW } from "constants/Routes";
import { getBUFromLocalStorage } from "helpers/storage";
import { useEventRequest, useStateSpecies } from "hooks";
import { GroupAnimalsList } from "groups/GroupAnimalsList";

export const Group = () => {
  const { groupId } = useParams<LivestockGroupPageParams>();
  const history = useHistory();
  const { eventsRequest } = useEventRequest();
  const businessUnitId = getBUFromLocalStorage();
  const { active: speciesActive } = useStateSpecies();
  const [addingAnimalsToGroup, setAddingAnimalsToGroup] = React.useState<boolean>(false);

  useEffect(() => {
    eventsRequest({ name: "group-view", details: { groupId } });
  }, [eventsRequest, groupId]);

  const { data, loading, error } = useGroupQuery({
    variables: {
      group: groupId,
      businessUnit: businessUnitId,
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });
  const group = data?.group || undefined;

  if (error) {
    return <Redirect to={GROUPS} />;
  }

  if (!group) {
    if (loading) return <LoadingOverlay customStyles={{ top: 0, left: 0 }} />;

    return <Redirect to={GROUPS} />;
  }

  if (speciesActive) {
    const isGroupDifferentSpecies = group.animalType?.id !== speciesActive.id;
    if (isGroupDifferentSpecies) return <Redirect to={GROUPS} />;
  }

  return (
    <>
      <PageHeader>
        <Breadcrumbs
          rootPaths={[
            {
              path: "Groups",
              route: GROUPS,
            },
          ]}
          currentPath="Group Information"
        />

        <Header backButton title={group.name || ""} />

        <GroupInfoBlock group={group}>
          {!addingAnimalsToGroup && (
            <Button
              id={ELEMENTS_IDS.EDIT_GROUP_BUTTON}
              caption="Edit group"
              onClick={(): void => history.push(`${GROUP_VIEW}/edit/${group.id}`)}
            />
          )}
        </GroupInfoBlock>

        <Spacer baselineHeight={3} />
      </PageHeader>

      <PageContent>
        <GroupAnimalsList
          groupId={group.id}
          addingAnimalsToGroup={addingAnimalsToGroup}
          setAddingAnimalsToGroup={setAddingAnimalsToGroup}
        />
      </PageContent>
    </>
  );
};
